/* =====================================================================
 * @auw/i18n — BCP-47 → AppLocale resolver.
 *
 * Keeps the five-locale surface (`en`, `ko`, `ja`, `zh-CN`, `zh-TW`) as
 * a small, auditable list so the host app can decide whether to ship
 * the full bundle or a locale-pruned subset later. The Intl locale map
 * below is kept separate because some callers (date / number
 * formatters) want a region-specific BCP-47 tag whereas i18next only
 * knows the short app locale id.
 * ===================================================================== */

export const SUPPORTED_LOCALES = ['en', 'ko', 'ja', 'zh-CN', 'zh-TW'] as const;
export type AppLocale = (typeof SUPPORTED_LOCALES)[number];

/**
 * Map a browser / user preferredLanguage BCP-47 tag to one of the five
 * locales the app actually ships translations for. Anything we do not
 * recognise falls back to English.
 */
export function resolveAppLocale(preferredLanguage?: string | null): AppLocale {
  if (!preferredLanguage) return 'en';

  const lang = preferredLanguage.toLowerCase().replace(/_/g, '-');

  if (lang.startsWith('ko')) return 'ko';
  if (lang.startsWith('ja')) return 'ja';
  if (lang === 'zh-tw' || lang === 'zh-hant' || lang === 'zh-hant-tw' || lang === 'zh-hk') return 'zh-TW';
  if (lang === 'zh-cn' || lang === 'zh-hans' || lang === 'zh-hans-cn') return 'zh-CN';
  if (lang.startsWith('zh')) return 'zh-CN';

  return 'en';
}

const INTL_LOCALE_MAP: Record<AppLocale, string> = {
  en: 'en-US',
  ko: 'ko-KR',
  ja: 'ja-JP',
  'zh-CN': 'zh-CN',
  'zh-TW': 'zh-TW',
};

export function toIntlLocale(appLocale: AppLocale): string {
  return INTL_LOCALE_MAP[appLocale] ?? 'en-US';
}
